import React, { useState, useEffect } from "react";
import axios from "axios";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { API } from "../../../contexts/AuthContext";
import { CURRENCY_RATES } from "../../../data/currencyRates";

function CurrencyConverter({ currencyCode = "USD" }) {
  const [rates, setRates] = useState(null);
  const [eurAmt, setEurAmt] = useState("100");
  const [localAmt, setLocalAmt] = useState("");

  useEffect(() => {
    if (currencyCode === "EUR") return;
    axios.get(`${API}/currency/rates`)
      .then(({ data }) => {
        setRates(data.rates || {});
        const rate = (data.rates || {})[currencyCode];
        if (rate && eurAmt) setLocalAmt((parseFloat(eurAmt) * rate).toFixed(2));
      })
      .catch(() => {
        // Fallback aux taux statiques si l'API est inaccessible
        const staticRates = Object.fromEntries(
          Object.entries(CURRENCY_RATES).map(([k, v]) => [k, v.rate])
        );
        setRates(staticRates);
        const staticRate = staticRates[currencyCode];
        if (staticRate && eurAmt) setLocalAmt((parseFloat(eurAmt) * staticRate).toFixed(2));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode]);

  const rate = rates?.[currencyCode];
  const handleEur = (v) => { setEurAmt(v); if (rate && v) setLocalAmt((parseFloat(v) * rate).toFixed(2)); else setLocalAmt(""); };
  const handleLocal = (v) => { setLocalAmt(v); if (rate && v) setEurAmt((parseFloat(v) / rate).toFixed(2)); else setEurAmt(""); };

  if (currencyCode === "EUR") return null;

  return (
    <div className="card-surface mt-5 border-l-4 border-[#C17F59]">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCw size={14} strokeWidth={1.5} className="text-[#C17F59]" />
        <span className="text-sm font-medium text-[#2B2623]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>Convertisseur de devises</span>
        {rate && <span className="text-xs text-[#9A8F86] ml-auto italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>1 EUR = {rate.toFixed(4)} {currencyCode}</span>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8B5E34] mb-1 block" style={{ fontFamily: "'Lora', Georgia, serif" }}>Euros (€)</label>
          <input type="number" className="input-field-box text-sm" value={eurAmt} onChange={(e) => handleEur(e.target.value)} data-testid="converter-eur-input" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8B5E34] mb-1 block" style={{ fontFamily: "'Lora', Georgia, serif" }}>{currencyCode}</label>
          <input type="number" className="input-field-box text-sm" value={localAmt} onChange={(e) => handleLocal(e.target.value)} data-testid="converter-local-input" />
        </div>
      </div>
    </div>
  );
}

export function BudgetTab({ result, currencyCode }) {
  const b = result?.budget_estime;
  const budgetItems = [
    { label: "Vols A/R (par personne)", key: "vols_aller_retour" },
    { label: "Hébergement total", key: "hebergement_total" },
    { label: "Activités & visites", key: "activites_total" },
    { label: "Restauration", key: "repas_total" },
    { label: "Transport local", key: "transport_local" },
    { label: "Divers & imprévus (10%)", key: "divers" },
  ];

  return (
    <div className="space-y-5">
      {b && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {budgetItems.map((item) => b[item.key] && (
              <div key={item.key} className="card-surface" data-testid={`budget-item-${item.key}`}>
                <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>{item.label}</p>
                <p className="text-2xl font-semibold text-[#2B2623]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{b[item.key]}</p>
              </div>
            ))}
          </div>
          {b.total && (
            <div className="bg-[#C17F59] p-5 text-[#F9F6F0]" style={{ borderRadius: "2px" }} data-testid="budget-total">
              <p className="text-xs uppercase tracking-widest mb-1 opacity-80" style={{ fontFamily: "'Lora', Georgia, serif" }}>Total estimé tous voyageurs</p>
              <p className="text-4xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>{b.total}</p>
            </div>
          )}
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm">
            <p className="text-xs text-amber-800 flex items-start gap-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              <AlertTriangle size={12} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
              Ces estimations sont indicatives. Les prix réels varient selon la saison et les disponibilités.
            </p>
          </div>
        </>
      )}
      <CurrencyConverter currencyCode={currencyCode || "USD"} />
    </div>
  );
}
